import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart } from 'lucide-react';

const messages = [
    "This day is special... because of you",
    "50 days of memories, smiles, and love",
    "From Just Hi to forever in just 50 days",
    "Every moment with you counts",
    "Built with love, just for you",
];

interface Props {
    onComplete: () => void;
}

const CountdownScreen: React.FC<Props> = ({ onComplete }) => {
    const [count, setCount] = useState(50);
    const [showSkip, setShowSkip] = useState(false);
    const [messageIndex, setMessageIndex] = useState(0);

    const { t } = useLanguage();

    useEffect(() => {
        // Show skip button after a short delay
        const skipTimer = setTimeout(() => {
            setShowSkip(true);
        }, 5000);

        // Change message every 3 seconds
        const messageInterval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % messages.length);
        }, 3000);

        // Countdown timer
        const countdownTimer = setInterval(() => {
            setCount(prev => {
                if (prev <= 1) {
                    clearInterval(countdownTimer);
                    clearInterval(messageInterval);
                    setTimeout(onComplete, 1000);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            clearInterval(countdownTimer);
            clearInterval(messageInterval);
            clearTimeout(skipTimer);
        };
    }, [onComplete]);

    return (
        <div className="flex flex-col min-h-screen justify-center items-center px-6 relative overflow-hidden bg-romantic-light dark:bg-romantic-dark">
            {/* Floating decorative hearts */}
            <motion.div
                animate={{ y: [0, -20, 0], opacity: [0.1, 0.3, 0.1] }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="text-4xl absolute top-20 left-10 md:left-[20%]"
            >
                💖
            </motion.div>
            <motion.div
                animate={{ y: [0, 20, 0], opacity: [0.1, 0.3, 0.1] }}
                transition={{ repeat: Infinity, duration: 5 }}
                className="text-4xl absolute top-10 right-10 md:right-[20%]"
            >
                💕
            </motion.div>
            <motion.div
                animate={{ x: [0, 20, 0], opacity: [0.1, 0.3, 0.1] }}
                transition={{ repeat: Infinity, duration: 6 }}
                className="text-4xl absolute bottom-20 left-12 md:left-[15%]"
            >
                💗
            </motion.div>
            <motion.div
                animate={{ x: [0, -20, 0], opacity: [0.1, 0.3, 0.1] }}
                transition={{ repeat: Infinity, duration: 7 }}
                className="text-4xl absolute bottom-10 right-12 md:right-[15%]"
            >
                💓
            </motion.div>

            {/* Main content */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center w-full"
            >
                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-playfair font-bold text-center mb-10 text-primary-red leading-tight">
                    {t('countdown.title')}
                </h1>

                {/* Countdown number circle */}
                <div className="relative mb-5">
                    <motion.div
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="w-20 h-20 md:w-28 md:h-28 rounded-full flex justify-center items-center bg-primary-red/10 border-4 border-primary-red/30 relative"
                    >
                        <div className="absolute inset-0 rounded-full border-2 border-romantic-pink/20 m-3 animate-ping" />
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={count}
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.5 }}
                                className="text-2xl md:text-3xl font-bold text-primary-red"
                            >
                                {count}
                            </motion.span>
                        </AnimatePresence>
                    </motion.div>
                </div>

                {/* Message carousel */}
                <div className="h-20 flex items-center justify-center">
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={messageIndex}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-xl md:text-2xl font-poppins text-center px-4 dark:text-gray-200"
                        >
                            {messages[messageIndex]}
                        </motion.p>
                    </AnimatePresence>
                </div>

                {/* Decorative divider */}
                <div className="flex items-center space-x-4 w-full justify-center">
                    <div className="h-1 w-16 md:w-24 bg-romantic-pink rounded-full" />
                    <Heart className="text-primary-red fill-current" size={32} />
                    <div className="h-1 w-16 md:w-24 bg-romantic-pink rounded-full" />
                </div>

                {/* Story text box */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="glass p-8 rounded-3xl mb-2 shadow-xl border-white/20"
                >
                    <p className="text-lg font-poppins text-center leading-relaxed text-gray-700 dark:text-gray-300">
                        I wanted to do something special for our 50th day. <br /><br />
                        So I didn’t just plan a moment — I built this website, piece by piece, thinking of you every step of the way.<br /><br />
                        Every section, every detail, every line of code <br />
                        was just my way of making you smile.<br /><br />
                        This is how important you are to me. ❤️
                    </p>
                </motion.div>

                {/* Skip button */}
                <div className="h-16 flex items-center justify-center">
                    <AnimatePresence>
                        {showSkip && (
                            <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={onComplete}
                                className="px-10 py-4 rounded-full bg-primary-red text-white text-lg font-poppins font-bold shadow-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
                            >
                                <span>Can't wait? Skip countdown 💝</span>
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>

                {/* Hint text */}
                <p className="text-sm font-poppins text-center mt-8 opacity-60 text-gray-500 dark:text-gray-400">
                    {count} seconds until the surprise begins...
                </p>

                {/* Progress dots */}
                <div className="flex space-x-2 mt-4">
                    {messages.map((_, index) => (
                        <div
                            key={index}
                            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === messageIndex ? 'bg-primary-red w-6' : 'bg-romantic-pink opacity-50'
                                }`}
                        />
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

export default CountdownScreen;
